import React, { createContext, useContext, useState, useEffect } from 'react';
import { initializeAuthBridge, validateAuthData } from '../utils/authBridge';

const UserContext = createContext();

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Initialize auth bridge to get authentication data from main app
    const initializeAuth = async () => {
      try {

        // Try to get auth data via multiple methods (URL params, postMessage, localStorage)
        const authData = await initializeAuthBridge();

        if (!authData || !validateAuthData(authData)) {
          setLoading(false);
          // Don't redirect here - let ProtectedRoute handle redirects
          return;
        }


        // Extract user data
        let userData = authData.userData || {};

        // Set user with available data
        const finalUser = {
          name: userData.name || `${authData.first_name} ${authData.last_name}`.trim() || authData.email?.split('@')[0] || 'User',
          organization: userData.organization || "Fouray's organization",
          plan: userData.plan || 'Community Plan',
          email: authData.email || userData.email || 'user@example.com',
          first_name: authData.first_name,
          last_name: authData.last_name,
          api_key: authData.api_key,
        };

        setUser(finalUser);
        setLoading(false);

      } catch (error) {
        console.error('Error initializing authentication:', error);
        setLoading(false);
        // Don't redirect here - let ProtectedRoute handle redirects
      }
    };

    initializeAuth();

    // Listen for storage changes (when auth data is updated or user logs out)
    const handleStorageChange = (e) => {
      if (e.key === 'api_key' && !e.newValue) {
        // User logged out - redirect to login page
        window.location.href = '/login';
      } else if (e.key === 'user-data' && e.newValue) {
        try {
          const newUser = JSON.parse(e.newValue);
          setUser(prevUser => ({ ...prevUser, ...newUser }));
        } catch (error) {
          console.error('Error parsing updated user data:', error);
        }
      } else if (e.key === 'email' && e.newValue) {
        setUser(prevUser => ({
          ...prevUser,
          email: e.newValue,
          name: e.newValue.split('@')[0]
        }));
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const updateUser = (userData) => {
    setUser(userData);
    try {
      localStorage.setItem('user-data', JSON.stringify(userData));
    } catch (error) {
      console.error('Error saving user data:', error);
    }
  };

  return (
    <UserContext.Provider value={{ user, setUser: updateUser, loading }}>
      {children}
    </UserContext.Provider>
  );
};
